/**
 * Whether the chat surface can be shown at all — and, when it cannot, why.
 *
 * Two reads have to land before the panel is a chat rather than a text box: this conversation's
 * stored transcript, and the MCP server's tool list. Neither is a decoration. A transcript that
 * failed to load would render as an empty thread, i.e. as a conversation the user never had; a tool
 * list that failed to load leaves the model holding only the five client-side tools, so it answers —
 * truthfully, given what it was handed — that it cannot do anything in the product. That second one
 * is the "useless chat": a composer that accepts questions and a bot that denies its own
 * capabilities, which reads as a broken assistant rather than an unavailable one.
 *
 * So both are PRECONDITIONS and both fail HARD, onto the same wall (SphereAIThread's unavailability
 * wall) — with the cause carried along, because "we couldn't load this conversation" and "Sphere AI
 * is unavailable" are different facts and only one of them is true at a time.
 */
export type SphereAIReadiness = "loading" | "ready" | "error";

export type SphereAIChatGate =
	| { status: "loading" }
	| { status: "ready" }
	| {
			status: "unavailable";
			/** Which precondition failed: the assistant's tool discovery, or this conversation's read. */
			cause: "assistant" | "conversation";
			/** The server's own `Retry-After` in ms when the failure was a 429; see ./rateLimit.ts. */
			retryAfterMs?: number;
			/** Re-runs the read that failed. */
			onRetry?: () => Promise<void>;
	  };

/** A settled read of one precondition, as the gate needs it. */
export type SphereAIPreconditionState = {
	status: SphereAIReadiness;
	retryAfterMs?: number;
	onRetry?: () => Promise<void>;
};

/**
 * How MCP tool discovery stands, from the query that performs it.
 *
 * An EMPTY tool list counts as a failure, not as a success with nothing in it. A sphere-mcp that
 * advertises no tools cannot serve a single thing the user opens this panel to ask for, and the
 * difference between "the request failed" and "the request returned nothing usable" is invisible
 * from where the user sits — both end in an assistant that can only talk about itself.
 *
 * A fetch in flight is not a verdict: while one is running (including React Query's own retries, and
 * a retry the user asked for from the wall) this is `"loading"`, so the wall yields to the loader
 * rather than sitting under a spinner the user just pressed for.
 */
export function toolDiscoveryStatus(query: {
	data: readonly unknown[] | undefined;
	isFetching: boolean;
	isError: boolean;
	isSuccess: boolean;
}): SphereAIReadiness {
	if (query.data !== undefined && query.data.length > 0) {
		return "ready";
	}
	if (query.isFetching) {
		return "loading";
	}
	// Settled with nothing usable — a failure, or a success that advertised no tools.
	return query.isError || query.isSuccess ? "error" : "loading";
}

/**
 * The single answer the Thread renders from, folded out of the two preconditions.
 *
 * Tool discovery is checked FIRST because it is the broader outage: when the MCP endpoint is
 * unreachable the transcript read is usually failing too (same deployment, same session), and of the
 * two facts "Sphere AI is unavailable" is the one that explains the panel. Naming the conversation
 * instead would invite the user to reopen another one and meet the same wall.
 */
export function sphereAIChatGate(preconditions: {
	tools: SphereAIPreconditionState;
	conversation: SphereAIPreconditionState;
}): SphereAIChatGate {
	const { tools, conversation } = preconditions;
	if (tools.status === "error") {
		return { status: "unavailable", cause: "assistant", retryAfterMs: tools.retryAfterMs, onRetry: tools.onRetry };
	}
	if (conversation.status === "error") {
		return {
			status: "unavailable",
			cause: "conversation",
			retryAfterMs: conversation.retryAfterMs,
			onRetry: conversation.onRetry,
		};
	}
	if (tools.status === "loading" || conversation.status === "loading") {
		return { status: "loading" };
	}
	return { status: "ready" };
}
